import React, { useEffect, useRef } from 'react';
import './Article.scss';
import { gsap } from './gsap';
import { createPopper } from '@popperjs/core';
import Hero from './Components/Hero/Hero';
import Header from './Components/common-header/Header';
import Footer from './Components/common-footer/Footer';
import InlinePhoto from './Components/Photos/InlinePhoto';
import Chapter1 from './Components/Chapters/Chapter1';
import Chapter2 from './Components/Chapters/Chapter2';
import Chapter3 from './Components/Chapters/Chapter3';
import Chapter4 from './Components/Chapters/Chapter4';
import Chapter5 from './Components/Chapters/Chapter5';
import Chapter6 from './Components/Chapters/Chapter6';
import Chapter7 from './Components/Chapters/Chapter7';
import PullQuoteWImg from './Components/PullQuote/PullQuoteWImg';
import EmphasizedParagraph from './Components/EmphasizedParagraph/EmphasizedParagraph';
import contents from './contents';
import FullWidthPhoto from './Components/Photos/FullWidthPhoto';
import RelatedContents from './Components/RelatedContents/RelatedContents';
import CtaButton from './Components/CtaButton/CtaButton';
// import Reference from './Components/Reference/Reference';

//for full width photos
import fullWidthImage1 from './assets/fullWidthImage1.jpg';
import fullWidthImage2 from './assets/fullWidthImage2.jpg';
import fullWidthImage3 from './assets/fullWidthImage3.jpg';

//for pq images
import pqImg1 from './assets/pqImg1.jpg';
import pqImg2 from './assets/pqImg2.jpg';

function Article({ heroBackgroundType, inlinePhotoSize, logoPlacement }) {
  //create refs for photos here if you want to add scale animation on scroll
  const inline1 = useRef();
  const inline2 = useRef();
  const inline3 = useRef();
  const inline4 = useRef();
  const inline5 = useRef();
  const inline6 = useRef();
  const inline7 = useRef();
  const inline8 = useRef();
  const allPhotosRef = useRef();
  allPhotosRef.current = [inline1, inline2, inline3, inline4, inline5, inline6, inline7, inline8]; // put all photo refs in this array

  const photoSize = inlinePhotoSize !== undefined ? inlinePhotoSize : contents.inlinePhotos.size;

  //creates/shows tooltip element when mouse enters
  const handleMouseEnterTooltip = (e, currentRef) => {
    const content = currentRef.current;
    createPopper(e.target, content, {
      placement: 'bottom',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 5]
          }
        }
      ]
    });
    content.style.visibility = 'visible';
  };

  //hides tooltip element when mouse leaves
  const handleMouseLeaveTooltip = (currentRef) => {
    currentRef.current.style.visibility = 'hidden';
  };

  useEffect(() => {
    allPhotosRef.current.forEach((photo) => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: photo.current,
          start: '20% bottom',
          end: 'top center',
          scrub: 1
        }
      });
      tl.to(photo.current, {
        width: '100%',
        height: '100%',
        duration: 3
      });
    });
  }, []);

  return (
    <div className="article">
      <Header clientName={contents.header} />
      <Hero
        handleMouseEnterTooltip={handleMouseEnterTooltip}
        handleMouseLeaveTooltip={handleMouseLeaveTooltip}
        heroBackgroundType={heroBackgroundType}
        logoPlacement={logoPlacement}
      />
      <Chapter1 />
      <FullWidthPhoto
        photo={fullWidthImage1}
        alt={'sleeping koala'}
        caption={
          'Experts believe that tens of thousands of koalas died in the 2019 and 2020 Australian bushfires. These deaths and associated habitat losses have pushed koalas onto the endangered species list. (Unsplash)'
        }
      />

      <Chapter2 />
      <InlinePhoto
        photoSize={photoSize} //this is for Storybook. can be deleted
        photoContents={contents.inlinePhotos.contents[0]} //pass in corresponding data
        photoRef={inline1} //pass in ref
      />

      <Chapter3 />
      <PullQuoteWImg
        quote={
          'Time is really critical for a lot of wildlife species globally, but it’s particularly important for a lot of our species here in Australia.'
        }
        author={'Dr. Carolyn Hogg'}
        descrip={'University of Sydney Senior Research Manager'}
        photo={pqImg1}
      />
      <FullWidthPhoto
        photo={fullWidthImage2}
        alt={'aws'}
        caption={
          'Scientists with the Threatened Species Initiative (TSI) study genetic diversity in the laboratory, extracting DNA/RNA and sequencing genes. This information gets compiled into enormous data sets, which are then analyzed for insights to guide conservation efforts. (Shutterstock)'
        }
      />

      <Chapter4 />
      <InlinePhoto
        photoSize={photoSize} //this is for Storybook. can be deleted
        photoContents={contents.inlinePhotos.contents[1]} //pass in corresponding data
        photoRef={inline2} //pass in ref
      />
      <Chapter5 />
      <FullWidthPhoto
        photo={fullWidthImage3}
        alt={'forest on fire'}
        caption={
          'Evidence suggests that climate change will lead to more intense wildfires and bushfires. As such, Australian researchers recognize the urgency of accelerating efforts to conserve threatened species. Using RONIN and AWS, scientists can analyze data much faster than before. For instance, a gene sequencing task that previously required 10 days can now be completed in just 6 hours. (Shutterstock)'
        }
      />
      <PullQuoteWImg
        quote={
          'If you can run through a workload in a couple of days rather than a couple of months, you then have those couple of months to ask the new questions [...] that’s the biggest advantage.'
        }
        author={'Parice Brandies'}
        descrip={'TSI team member'}
        photo={pqImg2}
      />
      <Chapter6 />
      <InlinePhoto
        photoSize={photoSize} //this is for Storybook. can be deleted
        photoContents={contents.inlinePhotos.contents[2]} //pass in corresponding data
        photoRef={inline3} //pass in ref
      />
      <Chapter7 />

      <EmphasizedParagraph />
      <CtaButton
        handleMouseEnterTooltip={handleMouseEnterTooltip}
        handleMouseLeaveTooltip={handleMouseLeaveTooltip}
      />
      {/* <div className="article__divider" /> */}
      {/* <Reference /> */}
      <RelatedContents />
      <Footer />
    </div>
  );
}

export default Article;
